import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaEnvelope, FaLinkedin, FaCheckCircle } from "react-icons/fa";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1 },
  }),
};

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In production, wire this to your form backend (e.g., Formspree, HubSpot, etc.)
    setSubmitted(true);
  };

  return (
    <main className="bg-white text-gray-800 font-sans">

      {/* ── Hero ── */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-24 px-6 xl:px-24">
        <div className="max-w-3xl mx-auto text-center">
          <motion.p
            className="text-sky-300 text-sm font-semibold uppercase tracking-widest mb-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          >
            Contact Us
          </motion.p>
          <motion.h1
            className="text-4xl md:text-5xl font-extrabold mb-6"
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          >
            Let's Talk About Your Transformation
          </motion.h1>
          <motion.p
            className="text-blue-200 text-lg"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          >
            Schedule a free 30-minute consultation. No sales pitch — just an honest conversation about your challenges and how we can help.
          </motion.p>
        </div>
      </section>

      {/* ── Contact Form + Info ── */}
      <section className="py-24 px-6 xl:px-24 bg-gray-50">
        <div className="max-w-6xl mx-auto grid md:grid-cols-5 gap-12">

          {/* Contact Info */}
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="md:col-span-2 space-y-8"
          >
            <div>
              <h2 className="text-2xl font-bold text-blue-900 mb-3">Get in Touch</h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                Our team typically responds within one business day. For urgent inquiries, please email us directly.
              </p>
            </div>

            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="p-2.5 rounded-lg bg-blue-50 flex-shrink-0">
                  <FaEnvelope className="text-blue-700 text-lg" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Email</p>
                  <a href="mailto:founder@xkminne.com" className="text-blue-700 font-medium hover:underline text-sm">
                    founder@xkminne.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-2.5 rounded-lg bg-blue-50 flex-shrink-0">
                  <FaLinkedin className="text-blue-700 text-lg" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">LinkedIn</p>
                  <a
                    href="https://www.linkedin.com/company/xkminne"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 font-medium hover:underline text-sm"
                  >
                    XKMINNE Technologies
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-2xl p-6 border border-blue-100">
              <h3 className="font-bold text-blue-900 mb-4">What to Expect</h3>
              <ul className="space-y-3">
                {[
                  "Response within 1 business day",
                  "Free 30-min discovery call with a domain expert",
                  "No obligation, no hard sell",
                  "Custom recommendations for your environment",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                    <FaCheckCircle className="text-emerald-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            variants={fadeUp} custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="md:col-span-3 bg-white rounded-2xl border border-gray-100 shadow-sm p-8 md:p-10"
          >
            {submitted ? (
              <div className="text-center py-16">
                <FaCheckCircle className="text-emerald-500 text-5xl mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-blue-900 mb-3">Message Received</h3>
                <p className="text-gray-600">
                  Thank you for reaching out. A member of our team will be in touch within one business day.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <h3 className="text-xl font-bold text-blue-900 mb-6">Request a Free Consultation</h3>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="Jane Smith"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm transition"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      placeholder="Acme Life Sciences"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm transition"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      Work Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="jane@company.com"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm transition"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      Phone (Optional)
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 000-0000"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Area of Interest
                  </label>
                  <select
                    name="service"
                    value={form.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm transition bg-white"
                  >
                    <option value="">Select a service area...</option>
                    <option value="manufacturing-ai">Manufacturing AI & Smart Operations</option>
                    <option value="mes">Life Sciences MES & Compliance</option>
                    <option value="sap">SAP Supply Chain Transformation</option>
                    <option value="digital-twin">Digital Twin Execution</option>
                    <option value="regulatory">Regulatory Affairs & Validation</option>
                    <option value="data-ai">Data & AI Platform Engineering</option>
                    <option value="other">Other / Not Sure Yet</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Tell Us About Your Challenge <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Briefly describe your current situation, goals, or the problem you're trying to solve..."
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm transition resize-none"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-700 hover:bg-blue-800 text-white font-bold py-4 rounded-lg shadow-sm transition-colors text-base"
                >
                  Request My Free Consultation
                </button>

                <p className="text-xs text-gray-400 text-center">
                  By submitting this form, you agree to be contacted by XKMINNE Technologies. We never share your information with third parties.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-6 text-center">
        <span className="text-xl font-extrabold text-white">
          XK<span className="text-sky-400">MINNE</span>
        </span>
        <p className="text-sm mt-3 text-gray-500">
          &copy; {new Date().getFullYear()} XKMINNE Technologies. All rights reserved.
        </p>
      </footer>
    </main>
  );
}
